import React, { useState, useEffect, useCallback } from 'react';
import { getAccessToken } from '@/lib/tokenStore';
import { Loader2, Wrench, CheckCircle2, AlertTriangle, XCircle } from 'lucide-react';
import { Badge } from '@/components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const hdrs = () => {
  const t = getAccessToken();
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const RusttakenOverzicht = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/management/rusttaken`, { headers: hdrs() });
      if (res.ok) setData(await res.json());
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-[#1f2548]" /></div>;
  }

  const locaties = data?.locaties || [];

  return (
    <div className="space-y-6 max-w-5xl mx-auto px-4 sm:px-0" data-testid="rusttaken-overzicht-page">
      <div>
        <h1 className="text-2xl font-bold text-[#1f2548] flex items-center gap-2" data-testid="rusttaken-overzicht-title">
          <Wrench className="w-6 h-6" /> Rusttaken Overzicht
        </h1>
        <p className="text-slate-500">Status per locatie</p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto" data-testid="overzicht-table">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-slate-200 text-left bg-slate-50">
                <th className="py-3 px-4 font-semibold text-slate-700">Locatie</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Rustmodus</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Achterstallig</th>
                <th className="py-3 px-4 font-semibold text-slate-700 text-center">Aan de beurt</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {locaties.map(loc => (
                <tr key={loc.locatieId} className="hover:bg-slate-50" data-testid={`row-${loc.locatieNaam}`}>
                  <td className="py-3 px-4 font-medium text-slate-700">{loc.locatieNaam}</td>
                  <td className="py-3 px-4 text-center">
                    {loc.rustmodusActief ? (
                      <Badge className="bg-blue-100 text-blue-700">Aan</Badge>
                    ) : (
                      <span className="text-slate-400">Uit</span>
                    )}
                  </td>
                  {/* 4.2: geen rusttaken (bv. kantoor/truckshop) toont "—", niet een
                      lege cel of een misleidend vinkje */}
                  <td className="py-3 px-4 text-center" data-testid={`achterstallig-${loc.locatieNaam}`}>
                    {(loc.totaal ?? 0) === 0 ? (
                      <span className="text-slate-300" title="Geen rusttaken voor deze locatie">—</span>
                    ) : (loc.achterstallig ?? 0) > 0 ? (
                      <Badge className="bg-red-100 text-red-700">{loc.achterstallig}</Badge>
                    ) : (
                      <CheckCircle2 className="w-5 h-5 text-green-500 mx-auto" />
                    )}
                  </td>
                  <td className="py-3 px-4 text-center" data-testid={`aan-de-beurt-${loc.locatieNaam}`}>
                    {(loc.totaal ?? 0) === 0 ? (
                      <span className="text-slate-300">—</span>
                    ) : (loc.aan_de_beurt ?? 0) > 0 ? (
                      <Badge className="bg-amber-100 text-amber-700">{loc.aan_de_beurt}</Badge>
                    ) : (
                      <span className="text-slate-400">0</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default RusttakenOverzicht;
